import { localSandboxEnabled, localSession } from '../utils/localSandbox';
import { saveTokens } from '../utils/tokenManager';
import { STORAGE_KEYS } from '../constants/storage';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp } from '@aws-amplify/auth';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import './LoginPage.css';
import { normalizeSignupPhone } from '../utils/normalizeSignupPhone';


export function SignupPage() {
  const navigate = useNavigate();
  const [method, setMethod] = useState('email'); // 'email' | 'phone'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingUsername, setExistingUsername] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setError(null);
    setExistingUsername(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setExistingUsername(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const usingEmail = method === 'email';
    let username;
    let phone;
    const email = formData.email.trim().toLowerCase();
    try {
      phone = formData.phone.trim() ? normalizeSignupPhone(formData.phone) : undefined;
      username = usingEmail ? email : phone;
    } catch (err) {
      setError(err.message);
      return;
    }

    const userAttributes = {
      given_name: formData.firstName,
      family_name: formData.lastName,
    };
    if (email) userAttributes.email = email;
    if (phone) userAttributes.phone_number = phone;

    setLoading(true);
    try {
      if (localSandboxEnabled) {
        const { tokens } = await localSession('signup', { email: username, password: formData.password, firstName: formData.firstName, lastName: formData.lastName });
        saveTokens(tokens.idToken, null, tokens.accessToken);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({ role: 'customer' }));
        window.location.replace('/dashboard');
        return;
      }
      const result = await signUp({
        username,
        password: formData.password,
        options: { userAttributes },
      });


      navigate(`/verify?username=${encodeURIComponent(username)}&destination=${encodeURIComponent(result.nextStep?.codeDeliveryDetails?.destination || email)}`);
    } catch (err) {
      if (err.name === 'UsernameExistsException') setExistingUsername(username);
      setError(err.name === 'UsernameExistsException'
        ? 'An account already exists. Sign in or reset your password. If you have not verified it yet, use the verification page to resend your code.'
        : err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="tg-auth-card tg-auth-card-wide">
        <div className="tg-auth-brand">
          <div className="tg-auth-brand-mark">T</div>
          <span className="tg-auth-brand-name">Tegrat Auto</span>
        </div>

        <h2 className="tg-auth-title">Create your account</h2>
        <p className="tg-auth-subtitle">Join Tegrat Auto Rental today</p>

        <div className="d-flex gap-4 mb-4 justify-content-center">
          <Form.Check
            type="radio"
            id="method-email"
            label="Email"
            checked={method === 'email'}
            onChange={() => handleMethodChange('email')}
            disabled={loading}
          />
          <Form.Check
            type="radio"
            id="method-phone"
            label="Phone Number"
            checked={method === 'phone'}
            onChange={() => handleMethodChange('phone')}
            disabled={loading}
          />
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="firstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Jane"
                  disabled={loading}
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="lastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Smith"
                  disabled={loading}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {method === 'email' ? (
            <>
              <Form.Group className="mb-3" controlId="email" key="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={loading}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="phone" key="phone">
                <Form.Label>Phone Number <span className="text-muted small">(optional)</span></Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 000-0000"
                  disabled={loading}
                />
              </Form.Group>
            </>
          ) : (
            <>
              <Form.Group className="mb-3" controlId="phone" key="phone">
                <Form.Label>
                  Phone Number{' '}
                  <span className="text-muted small">
                    (Msg &amp; data rates may apply)
                  </span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 000-0000"
                  disabled={loading}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="email" key="email">
                <Form.Label>Email <span className="text-muted small">(for verification)</span></Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={loading}
                  required
                />
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3" controlId="password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              placeholder="At least 8 characters"
              disabled={loading}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="confirmPassword">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              disabled={loading}
              required
            />
          </Form.Group>

          {error && <Alert variant="danger">{error}{existingUsername && <> <Link to={`/verify?username=${encodeURIComponent(existingUsername)}`}>Verify an existing signup</Link></>}</Alert>}

          <div className="d-grid gap-2">
            <Button variant="primary" type="submit" disabled={loading} size="lg">
              {loading ? 'Creating account…' : 'Create Account'}
            </Button>
          </div>
        </Form>

        <hr className="tg-auth-divider" />
        <div className="tg-auth-footer">
          Already have an account? <Link to="/">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
